import React, { useEffect, useState, useRef } from 'react';
import { Alert, Card, Col, Offcanvas, Row } from 'react-bootstrap';
import styles from './DeviceDetailsOffCanvas.module.css';
import DateField from '../common/DateField';
import { useTranslation } from 'react-i18next';
import useFetchDeviceByEan from '../../hooks/useFetchDeviceByEan';
import DeviceDetailsCrmInfos from './DeviceDetailsCrmInfo';
import DeviceDetailsServices from './DeviceDetailsServices';
import DeviceDetailsPurchases from './DeviceDetailsPurchases';
import DeviceDetailsCrm from './DeviceDetailsCrm';
import useFetchDeviceServiceByEan from 'hooks/useFetchDeviceServiceByEan';

interface DeviceDetailsOffCanvasProps {
  show: boolean;
  handleClose: () => void;
  ean: string;
}

const DeviceDetailsOffCanvas: React.FC<DeviceDetailsOffCanvasProps> = ({ show, handleClose, ean }) => {
  const { t } = useTranslation();
  const [currentEan, setCurrentEan] = useState<string | null>(null);

  const previousEanRef = useRef<string | null>(null);

  const { deviceResponse, error } = useFetchDeviceByEan({ ean: currentEan || '' });
  const { serviceResponse } = useFetchDeviceServiceByEan({ ean: ean || '' });
  const hasServices = serviceResponse?.length > 0;
  const colSize = hasServices ? 4 : 6;

  useEffect(() => {
    if (show && ean && ean !== previousEanRef.current) {
      setCurrentEan(ean); // Set the EAN only if it has changed
      previousEanRef.current = ean; // Update the previous EAN ref
    }
  }, [show, ean]);


  return (
    <>
      <Offcanvas show={show} onHide={handleClose} placement="end" className={styles.offcanvasWide}>
        <Offcanvas.Header className='bg-secondary text-white' closeButton>
          <Offcanvas.Title className='position-relative top-0 start-50 translate-middle-x fw-medium fs-2'>{t("infosUser")} {deviceResponse.ean}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Card className='p-2 border border-light border-2 shadow-sm rounded-3 w-75 position-relative top-0 start-50 translate-middle-x '>
            <Row className='align-items-center'>
              <Col className='d-flex justify-content-center'>
                <span className='fs-5 fw-semibold text-muted '>ID : </span>
                <span className='fs-5 fw-medium ms-1'>{deviceResponse.id}</span>
              </Col>
              <Col className='d-flex align-items-center fs-5 fw-semibold'>
                <span className='text-muted me-2'>{t("devices.lastapicall")}</span>
                <DateField date={deviceResponse.lastApiCall} />
              </Col>
            </Row>
          </Card>
          {error && (
            <Alert key={'danger'} variant={'danger'}>
              {t("error")}: {error}
            </Alert>
          )}
          {deviceResponse && (
            <>
              <Row>
                <Col md={colSize}>
                  <DeviceDetailsCrmInfos ean={deviceResponse?.ean} />
                </Col>
                <DeviceDetailsServices ean={ean} hasServices={hasServices} />
                <Col md={colSize}>
                  <Card className="shadow mt-3">
                    <Card.Header className="bg-primary text-white text-center clickable">
                      <Card.Title className="fs-3 fw-semibold my-2">{t("devices.dns.title")}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <Row className='align-items-center my-2'>
                        <Col md={12} className='d-flex justify-content-center'>
                          <span className='fs-5 fw-semibold text-muted'>{t("devices.dns.ip")} :</span>
                          <span className='fs-5 fw-medium ms-1'>{deviceResponse.dns?.ip}</span>
                        </Col>
                        <Col className='d-flex justify-content-center fs-5 fw-semibold'>
                          <span className='fs-5 fw-semibold text-muted'>{t("devices.dns.internalip")} :</span>
                          <span className='fs-5 fw-medium ms-1'>{deviceResponse.dns?.internalIP}</span>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <DeviceDetailsCrm ean={deviceResponse?.ean} />
              <DeviceDetailsPurchases ean={ean} />
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default DeviceDetailsOffCanvas;
